// Slideshow Class handles all flickity based sliders
import '@archetype-themes/vendors/flickity'
import '@archetype-themes/vendors/flickity-fade'
import { debounce } from '@archetype-themes/utils/utils'
import { HTMLThemeElement } from '@archetype-themes/custom-elements/theme-element'

class Slideshow {
  constructor(el, args) {
    this.el = el
    let drag = true
    if (el.getAttribute('draggable') == 'false') {
      drag = false
    }
    const defaults = {
      adaptiveHeight: false,
      autoPlay: false,
      draggable: drag,
      avoidReflow: false,
      childNav: null,
      childNavScroller: null,
      childVertical: false,
      dragThreshold: 7,
      fade: false,
      friction: 0.8,
      initialIndex: 0,
      pageDots: false,
      pauseAutoPlayOnHover: false,
      prevNextButtons: false,
      rightToLeft: document.documentElement.dir === 'rtl',
      selectedAttraction: 0.14,
      setGallerySize: true,
      wrapAround: true
    }

    this.args = Object.assign({}, defaults, args)

    this.classes = {
      animateOut: 'animate-out',
      isPaused: 'is-paused',
      isActive: 'is-active'
    }

    this.selectors = {
      allSlides: '.slideshow__slide',
      currentSlide: '.is-selected',
      wrapper: '.slideshow-wrapper',
      pauseButton: '.slideshow__pause'
    }

    this.productSelectors = {
      thumb: '.product__thumb-item:not(.hide)',
      links: '.product__thumb-item:not(.hide) a',
      arrow: '.product__thumb-arrow'
    }

    this.args.on = {
      ready: this.init.bind(this),
      change: this.slideChange.bind(this),
      settle: this.afterChange.bind(this)
    }

    if (this.args.childNav) {
      this.childNavEls = this.args.childNav.querySelectorAll(this.productSelectors.thumb)
      this.childNavLinks = this.args.childNav.querySelectorAll(this.productSelectors.links)
      this.arrows = this.args.childNav.querySelectorAll(this.productSelectors.arrow)
      if (this.childNavLinks.length) {
        this.initChildNav()
      }
    }

    if (this.args.avoidReflow) {
      avoidReflow(el)
    }

    this.slideshow = new Flickity(el, this.args)

    //this._observeComparisonLoad()

    if (el.dataset.zoom && el.dataset.zoom === 'true') {
      this.slideshow.on('dragStart', () => {
        this.slideshow.slider.style.pointerEvents = 'none'
        if (this.slideshow.options.fade) {
          this.slideshow.slider.querySelector('.is-selected').style.pointerEvents = 'none'
        }
      })
      this.slideshow.on('dragEnd', () => {
        this.slideshow.slider.style.pointerEvents = 'auto'
        if (this.slideshow.options.fade) {
          this.slideshow.slider.querySelector('.is-selected').style.pointerEvents = 'auto'
        }
      })
    }

    if (this.args.autoPlay) {
      const wrapper = el.closest(this.selectors.wrapper)
      this.pauseBtn = wrapper.querySelector(this.selectors.pauseButton)
      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', this._togglePause.bind(this))
      }
    }

    window.addEventListener(
      'resize',
      debounce(300, () => {
        this.resize()
      })
    )

    function avoidReflow(el) {
      if (!el.id) return
      let firstChild = el.firstChild
      while (firstChild != null && firstChild.nodeType == 3) {
        firstChild = firstChild.nextSibling
      }
      const style = document.createElement('style')
      style.innerHTML = `#${el.id} .flickity-viewport{height:${firstChild.offsetHeight}px}`
      document.head.appendChild(style)
    }
  }

  init() {
    this.currentSlide = this.el.querySelector(this.selectors.currentSlide)
    if (this.args.callbacks?.onInit instanceof Function) {
      this.args.callbacks.onInit(this.currentSlide)
    }
  }

  _observeComparisonLoad() {
    const checkAndAttach = () => {
      const buttons = this.el.querySelectorAll('.comparison__button')
      if (buttons.length) {
        buttons.forEach(btn => {
          btn.addEventListener('pointerdown', () => this.setDraggable(false))
          btn.addEventListener('pointerup', () => this.setDraggable(true))
          btn.addEventListener('mouseleave', () => this.setDraggable(true))
        })
        console.log('✅ comparison__button listeners attached (hydrated)')
        return true
      }
      return false
    }

    let attempts = 10
    const interval = setInterval(() => {
      if (checkAndAttach() || --attempts <= 0) {
        clearInterval(interval)
      }
    }, 300)
  }

  slideChange(index) {
    if (this.currentSlide) {
      document.dispatchEvent(new CustomEvent('slideshow-component:slide-changed', {
        detail: {
          previousSlide: this.currentSlide.previousElementSibling,
          currentSlide: this.currentSlide,
          nextSlide: this.currentSlide.nextElementSibling
        }
      }))
    }

    if (this.args.fade && this.currentSlide) {
      this.currentSlide.classList.add(this.classes.animateOut)
      this.currentSlide.addEventListener('transitionend', () => {
        this.currentSlide.classList.remove(this.classes.animateOut)
      })
    }

    if (this.args.childNav) {
      this.childNavGoTo(index)
    }

    if (this.args.callbacks?.onChange instanceof Function) {
      this.args.callbacks.onChange(index)
    }

    if (this.arrows && this.arrows.length) {
      this.arrows[0].classList.toggle('hide', index === 0)
      this.arrows[1].classList.toggle('hide', index === this.childNavLinks.length - 1)
    }
  }

  afterChange() {
    if (this.args.fade) {
      this.el.querySelectorAll(this.selectors.allSlides).forEach((slide) => {
        slide.classList.remove(this.classes.animateOut)
      })
    }
    this.currentSlide = this.el.querySelector(this.selectors.currentSlide)
    if (this.args.childNav) {
      this.childNavGoTo(this.slideshow.selectedIndex)
    }
  }

  destroy() {
    if (this.args.childNav && this.childNavLinks.length) {
      this.childNavLinks.forEach((a) => a.classList.remove(this.classes.isActive))
    }
    this.slideshow.destroy()
  }

  reposition() {
    this.slideshow.reposition()
  }

  _togglePause() {
    if (this.pauseBtn.classList.contains(this.classes.isPaused)) {
      this.pauseBtn.classList.remove(this.classes.isPaused)
      this.slideshow.playPlayer()
    } else {
      this.pauseBtn.classList.add(this.classes.isPaused)
      this.slideshow.pausePlayer()
    }
  }

  resize() {
    this.slideshow.resize()
  }

  play() {
    this.slideshow.playPlayer()
  }

  pause() {
    this.slideshow.pausePlayer()
  }

  goToSlide(i) {
    this.slideshow.select(i)
  }

  setDraggable(enable) {
    this.slideshow.options.draggable = enable
    this.slideshow.updateDraggable()
  }

  initChildNav() {
    this.childNavLinks[this.args.initialIndex].classList.add('is-active')

    this.childNavLinks.forEach((link, i) => {
      link.setAttribute('data-index', i)

      link.addEventListener('click', (evt) => {
        evt.preventDefault()
        this.goToSlide(this.getChildIndex(evt.currentTarget))
      })
      link.addEventListener('focus', (evt) => {
        this.goToSlide(this.getChildIndex(evt.currentTarget))
      })
      link.addEventListener('keydown', (evt) => {
        if (evt.keyCode === 13) {
          this.goToSlide(this.getChildIndex(evt.currentTarget))
        }
      })
    })

    if (this.arrows.length) {
      this.arrows.forEach((arrow) => {
        arrow.addEventListener('click', this.arrowClick.bind(this))
      })
    }
  }

  getChildIndex(target) {
    return parseInt(target.dataset.index)
  }

  childNavGoTo(index) {
    this.childNavLinks.forEach((a) => {
      a.blur()
      a.classList.remove(this.classes.isActive)
    })
    const el = this.childNavLinks[index]
    el.classList.add(this.classes.isActive)

    if (!this.args.childNavScroller) return

    if (this.args.childVertical) {
      this.args.childNavScroller.scrollTop = el.offsetTop - 100
    } else {
      this.args.childNavScroller.scrollLeft = el.offsetLeft - 100
    }
  }

  arrowClick(evt) {
    if (evt.currentTarget.classList.contains('product__thumb-arrow--prev')) {
      this.slideshow.previous()
    } else {
      this.slideshow.next()
    }
  }
}

class SlideshowSection extends HTMLThemeElement {
  connectedCallback() {
    super.connectedCallback()

    this.container = this.querySelector(`#SlideshowWrapper-${this.sectionId}`)
    this.slideshow = this.container.querySelector('#Slideshow-' + this.sectionId)
    this.initialIndex = 0

    if (!this.slideshow) return

    this.init()
  }

  init() {
    const slides = this.slideshow.querySelectorAll('.slideshow__slide')
    this.slideshow.classList.remove('loading', 'loading--delayed')
    this.slideshow.classList.add('loaded')

    if (slides.length > 1) {
      const sliderArgs = {
        prevNextButtons: this.slideshow.hasAttribute('data-arrows'),
        pageDots: this.slideshow.hasAttribute('data-dots'),
        fade: true,
        setGallerySize: false,
        initialIndex: this.initialIndex,
        autoPlay: this.slideshow.dataset.autoplay === 'true' ? parseInt(this.slideshow.dataset.speed) : false
      }
      this.flickity = new Slideshow(this.slideshow, sliderArgs)
    } else {
      slides[0].classList.add('is-selected')
    }
  }

  forceReload() {
    this.onSectionUnload()
    this.init()
  }

  onSectionUnload() {
    if (this.flickity?.destroy instanceof Function) {
      this.flickity.destroy()
    }
  }

  onSectionSelect() {
    this.forceReload()
  }

  onSectionReorder() {
    this.forceReload()
  }

  onSectionDeselect() {
    if (this.flickity?.play instanceof Function && this.flickity.args.autoPlay) {
      this.flickity.play()
    }
  }

  onBlockSelect({ detail: { blockId } }) {
    this.forceReload()
    const slide = this.slideshow.querySelector('.slideshow__slide--' + blockId)
    const index = parseInt(slide.dataset.index)

    if (this.flickity?.pause instanceof Function) {
      this.flickity.goToSlide(index)
      this.flickity.pause()
    } else {
      this.initialIndex = index
      setTimeout(() => {
        if (this.flickity?.pause instanceof Function) {
          this.flickity.pause()
        }
      }, 1000)
    }
  }

  onBlockDeselect() {
    if (this.flickity?.play instanceof Function && this.flickity.args.autoPlay) {
      this.flickity.play()
    }
  }
}

customElements.define('slideshow-section', SlideshowSection)

export { Slideshow }
export { SlideshowSection }

